import { useState } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useAuthStore } from "user/store/useAuthStore";
import Header from "user/components/common/Header";
import { usePutCustomer } from "common/hooks/user/account/usePutCustomer";
import { errorMsgHandler, regPassword } from "common/hooks/utils/utils";
import { usePutPassword } from "common/hooks/user/account/usePutPassword";
import useOverlay, { OverlayWrapper } from "common/hooks/user/useOverlay";
import { CompanySearchOverlay, SystemPopup } from "user/components/common/Overlay";
import { useGetCustomer } from "common/hooks/user/account/useGetCustomer";
import Spinner from "common/components/spinner";
import { ReactComponent as ArrowRight } from "../../assets/icons/rightArrow.svg";
import { ReactComponent as Close } from "../../assets/icons/close_rounded.svg";
import { ReactComponent as SelectIcon } from "../../../common/assets/icons/selectIcon.svg";

type InitialValuesProps = {
    m_password: string;
    new_password: string;
    new_password_check: string;
};
type CustomerProps = {
    customer_idx: number | null;
    customer_name: string;
};

const initialValues: InitialValuesProps = {
    m_password: "",
    new_password: "",
    new_password_check: "",
};

export default function MyPage() {
    const [showDepartment, setShowDepartment] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const user = useAuthStore(state => state);
    const onClickLogout = user.clearAuth;
    const { data: customerData } = useGetCustomer(Number(user.customer_idx));
    const { mutate: mutatePassword, isPending: isPendingPassword } = usePutPassword();
    const { mutate: mutateCustomer, isPending: isPendingCustomer } = usePutCustomer();
    const { overlay, openOverlay, closeOverlay } = useOverlay();

    const initialCompanyValues: CustomerProps = {
        customer_idx: customerData?.data.customer_idx!,
        customer_name: customerData?.data.customer_name ?? "",
    };

    // 비밀번호 변경
    const onSubmitChangePassword = async (valuse: InitialValuesProps) => {
        const body = {
            m_password: valuse.m_password,
            new_password: valuse.new_password,
        };

        mutatePassword(
            { ...body },
            {
                onSuccess: () => {
                    setShowChangePassword(false);
                },
                onError: error => {
                    const err_msg = errorMsgHandler(error);
                    openOverlay(<SystemPopup message={err_msg} onClickClose={closeOverlay} />);
                },
            },
        );
    };

    // 회사 정보 변경
    const onSubmitChangeCompany = (valuse: CustomerProps) => {
        const body = {
            customer_idx: customerData?.data.m_category === 1 ? valuse.customer_idx! : customerData?.data.customer_idx!,
            customer_name: valuse.customer_name,
        };

        mutateCustomer(
            { ...body },
            {
                onSuccess: () => {
                    setShowDepartment(false);
                },
                onError: error => {
                    const err_msg = errorMsgHandler(error);
                    openOverlay(<SystemPopup message={err_msg} onClickClose={closeOverlay} />);
                },
            },
        );
    };

    return (
        <div className="min-h-[100dvh] relative">
            {(isPendingPassword || isPendingCustomer) && <Spinner />}

            <Header goBack title="회원정보 수정" />

            <div className="wrapper py-[16px]">
                <ul className="flex flex-col gap-[12px]">
                    <li>
                        <p className="font-bold">회사</p>
                        <div
                            className="text-[#484964] py-[8px] flex justify-between items-center cursor-pointer"
                            onClick={() => setShowDepartment(true)}
                        >
                            <span className="flex-1">{customerData?.data.customer_name}</span>
                            <ArrowRight />
                        </div>
                    </li>
                    <li>
                        <p className="font-bold">이름</p>
                        <p className="text-[#484964] py-[8px]">{customerData?.data.m_name}</p>
                    </li>
                    <li>
                        <p className="font-bold">휴대폰번호</p>
                        <p className="text-[#484964] py-[8px]">{customerData?.data.m_mobile}</p>
                    </li>
                    <li>
                        <p className="font-bold">이메일</p>
                        <p className="text-[#484964] py-[8px]">{customerData?.data.m_email}</p>
                    </li>

                    <li>
                        <div
                            className="py-[8px] font-[500] flex justify-between items-center cursor-pointer"
                            onClick={() => setShowChangePassword(true)}
                        >
                            비밀번호 수정
                            <ArrowRight />
                        </div>
                    </li>
                </ul>
            </div>

            <div className="wrapper border-t-[8px] border-[#F3F3F8] py-[16px]">
                <button type="button" className="text-[#747883]" onClick={onClickLogout}>
                    로그아웃
                </button>
            </div>

            {showDepartment && (
                <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
                    <div className="max-h-[100dvh] absolute inset-0 bg-white flex flex-col">
                        <Header goBack title="회사 정보 수정" onClickGoBack={() => setShowDepartment(false)} />
                        <Formik
                            initialValues={initialCompanyValues}
                            validateOnMount
                            validate={values => {
                                const errors: Record<string, string> = {};
                                if (!values.customer_name) {
                                    errors.customer_name = "회사를 입력해주세요.";
                                }
                                return errors;
                            }}
                            onSubmit={(values, { setSubmitting }) => {
                                setSubmitting(true);
                                onSubmitChangeCompany(values);
                            }}
                        >
                            {({ isSubmitting, errors, values, setFieldValue, setValues, touched }) => {
                                const errorKeys = Object.keys(errors);
                                const isInValid = errorKeys.length > 0;
                                // console.log(errorKeys);

                                return (
                                    <Form className="flex flex-col flex-1">
                                        <div className="wrapper flex-1 flex flex-col justify-between py-[16px] w-full">
                                            <div className="flex flex-col gap-3">
                                                {/* 회사 회원일 떄 */}
                                                {user.m_category === 1 ? (
                                                    <div className={`flex flex-col`}>
                                                        <label htmlFor="customer_name" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            회사
                                                        </label>
                                                        <div
                                                            className={`overflow-hidden flex items-center justify-between rounded-xl border bg-white pr-[20px] ${touched.customer_name && errorKeys.includes("customer_name") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                            onClick={() =>
                                                                openOverlay(
                                                                    <CompanySearchOverlay
                                                                        category={1}
                                                                        onClickClose={closeOverlay}
                                                                        onClickTarget={({ name, idx }: { name: string; idx: number }) => {
                                                                            setFieldValue("customer_idx", idx);
                                                                            setFieldValue("customer_name", name);
                                                                            closeOverlay();
                                                                        }}
                                                                    />,
                                                                )
                                                            }
                                                        >
                                                            <input
                                                                name="customer_name"
                                                                id="customer_name"
                                                                readOnly
                                                                value={values.customer_name}
                                                                placeholder="회사와 부서를 검색하세요"
                                                                className="cursor-pointer p-3 w-full placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                            />
                                                            <SelectIcon />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className={`flex flex-col`}>
                                                        {/* 개인 회원일 떄 */}
                                                        <label htmlFor="customer_name" className="mb-[4px] text-[#484964] leading-[24px]">
                                                            회사
                                                        </label>
                                                        <div>
                                                            <div
                                                                className={`overflow-hidden flex justify-between items-center pr-[13px] rounded-xl border ${touched.customer_name && errorKeys.includes("customer_name") ? "[&:has(input)]:border-[#DC2626]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                            >
                                                                <Field
                                                                    id="customer_name"
                                                                    name="customer_name"
                                                                    placeholder="회사와 부서를 입력해주세요"
                                                                    className="flex-1 p-3 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                                />
                                                                {values.customer_name.length > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        title="내용 지우기"
                                                                        onClick={() => setFieldValue("customer_name", "")}
                                                                    >
                                                                        <Close />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <ErrorMessage
                                                                name="customer_name"
                                                                render={message => (
                                                                    <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                                        {message}
                                                                    </div>
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="submit"
                                                data-disabled={isInValid}
                                                className="shadow-custom bg-[#273219] text-white font-bold text-[18px] leading-[28px] py-[12px] rounded-[12px] w-full data-[disabled='true']:opacity-[0.3]"
                                            >
                                                저장
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </OverlayWrapper>
            )}
            {showChangePassword && (
                <OverlayWrapper overlay={overlay} closeOverlay={closeOverlay}>
                    <div className="max-h-[100dvh] absolute inset-0 bg-white flex flex-col">
                        <Header goBack title="비밀번호 수정" onClickGoBack={() => setShowChangePassword(false)} />
                        <Formik
                            initialValues={initialValues}
                            validateOnMount
                            validate={values => {
                                const errors: Record<string, string> = {};

                                if (!values.m_password || !regPassword.test(values.m_password)) {
                                    errors.m_password = "영문 숫자 조합 6자리 이상 입력하세요.";
                                }
                                if (!values.new_password || !regPassword.test(values.new_password)) {
                                    errors.m_password_check = "영문 숫자 조합 6자리 이상 입력하세요.";
                                }
                                if (!values.new_password_check || !regPassword.test(values.new_password_check)) {
                                    errors.m_password_check = "영문 숫자 조합 6자리 이상 입력하세요.";
                                }
                                if (values.new_password !== values.new_password_check) {
                                    errors.m_password_check = "비밀번호가 일치하지 않습니다.";
                                }

                                return errors;
                            }}
                            onSubmit={(values, { setSubmitting }) => {
                                setSubmitting(true);
                                onSubmitChangePassword(values);
                            }}
                        >
                            {({ isSubmitting, errors, values, setFieldValue, setValues, touched }) => {
                                const errorKeys = Object.keys(errors);
                                const isInValid = errorKeys.length > 0;
                                // console.log(errorKeys);

                                return (
                                    <Form className="flex flex-col flex-1">
                                        <div className="wrapper flex-1 flex flex-col justify-between py-[16px] w-full">
                                            <div className="flex flex-col gap-3">
                                                <div className={`flex flex-col`}>
                                                    <label htmlFor="m_password" className="mb-[4px] text-[#484964] leading-[24px]">
                                                        현재 비밀번호
                                                    </label>
                                                    <div
                                                        className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${touched.m_password && errorKeys.includes("m_password") ? "[&:has(input)]:border-[red]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                    >
                                                        <div className="flex justify-between items-center w-full">
                                                            <Field
                                                                type="password"
                                                                id="m_password"
                                                                name="m_password"
                                                                placeholder="비밀번호"
                                                                className="flex-1 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                            />
                                                        </div>
                                                    </div>
                                                    <ErrorMessage
                                                        name="m_password"
                                                        render={message => (
                                                            <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                                {message}
                                                            </div>
                                                        )}
                                                    />
                                                </div>

                                                <div className={`flex flex-col`}>
                                                    <label htmlFor="new_password" className="mb-[4px] text-[#484964] leading-[24px]">
                                                        새 비밀번호
                                                    </label>
                                                    <div
                                                        className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${touched.new_password && errorKeys.includes("new_password") ? "[&:has(input)]:border-[red]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                    >
                                                        <div className="flex justify-between items-center w-full">
                                                            <Field
                                                                type="password"
                                                                id="new_password"
                                                                name="new_password"
                                                                placeholder="영문 숫자 조합 6자리 이상"
                                                                // as={Input}
                                                                className="flex-1 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                            />
                                                            {values.new_password.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    title="내용 지우기"
                                                                    onClick={() => setFieldValue("new_password", "")}
                                                                >
                                                                    <Close />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ErrorMessage
                                                        name="new_password"
                                                        render={message => (
                                                            <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                                {message}
                                                            </div>
                                                        )}
                                                    />
                                                </div>

                                                <div className={`flex flex-col`}>
                                                    <label htmlFor="new_password_check" className="mb-[4px] text-[#484964] leading-[24px]">
                                                        새 비밀번호 재입력
                                                    </label>
                                                    <div
                                                        className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${touched.new_password_check && errorKeys.includes("new_password_check") ? "[&:has(input)]:border-[red]" : "[&:has(input:focus)]:border-[#4B632B]"}`}
                                                    >
                                                        <div className="flex justify-between items-center w-full">
                                                            <Field
                                                                type="password"
                                                                id="new_password_check"
                                                                name="new_password_check"
                                                                placeholder="영문 숫자 조합 6자리 이상"
                                                                className="flex-1 placeholder:text-[#ddd] text-lg focus:outline-none focus:ring-0"
                                                            />
                                                            {values.new_password_check.length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    title="내용 지우기"
                                                                    onClick={() => setFieldValue("new_password_check", "")}
                                                                >
                                                                    <Close />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <ErrorMessage
                                                        name="new_password_check"
                                                        render={message => (
                                                            <div className="text-[#DC2626] flex gap-[6px] text-[14px] mt-[4px]">
                                                                {message}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                data-disabled={isInValid}
                                                className="shadow-custom bg-[#273219] text-white font-bold text-[18px] leading-[28px] py-[12px] rounded-[12px] w-full data-[disabled='true']:opacity-[0.3]"
                                            >
                                                저장
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    </div>
                </OverlayWrapper>
            )}
        </div>
    );
}
